import { BlockStack, Button, Card, DataTable, EmptyState, InlineStack, Link, Text } from '@shopify/polaris';
import Pagination from 'components/pagination';
import SimpleFilter from 'components/simpleFilter';
import helpers from 'helpers/index';
import { ___ } from 'languages/index';
import {
  TypedVehicle_log,
  useGetVehiclelogs,
  useMutateDeleteVehiclelog,
  useMutateCreatePassengerListing,
  useMutateTripLogListing,
  useDownloadVehicleLog,
} from 'queries/vehicle_log.query';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dateandtime from 'date-and-time';
import emptyIMG from '../../media/images/empty.png';
import { DeleteIcon, EditIcon, FolderDownIcon, PageDownIcon, PlusIcon } from '@shopify/polaris-icons';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import ListLogEditModal from './list_log.edit.modal';

interface Props {
  vehicle_id: string;
}

function ListLog({ vehicle_id }: Props) {
  const history = useNavigate();
  const REACT_APP_AJAX_UPLOAD_PERMALINK = process.env.REACT_APP_AJAX_UPLOAD_PERMALINK;

  const { search } = useLocation();
  const StringQuery: any = helpers.ExtractUrl(search);

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 200,
      sort: 'start_time:desc',
      vehicle_id,
    }),
    []
  );

  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
    vehicle_id,
  });

  const [entities, setEntities] = useState<TypedVehicle_log[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentLogId, setCurrentLogId] = useState<string>(null);

  const { data, refetch: getEntities, isFetching: loadingVehicleLog } = useGetVehiclelogs(mainQuery);

  useEffect(() => {
    if (data) {
      setEntities(data.body || []);
      setTotalItems(data.totalItems || 0);
    }
  }, [data]);

  useEffect(() => {
    setMainQuery((prev) => ({ ...prev, vehicle_id }));
  }, [vehicle_id]);

  useEffect(() => {
    const buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) {
      history(`/vehicle/${vehicle_id}/log` + buildURLSearch);
    }
    getEntities();
  }, [mainQuery]);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prev) => ({ ...prev, page: numPage, limit }));
  }, []);

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        setMainQuery(initialQuery);
        return;
      }
      setMainQuery((prev) => ({ ...prev, ...value, page: 1 }));
    },
    [initialQuery]
  );

  /** Xóa thì hỏi  */
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: deleteVehicleLog } = useMutateDeleteVehiclelog();
  const deleteVehicleLogCallback = useCallback(
    async (vehicle_log_id: any) => {
      let confirmCancelTicket = await showDeleteConfirm('Bạn có chắc muốn xóa?', `Đây là việc không thể hoàn tác, vui lòng thao tác cẩn thận.`);
      try {
        if (confirmCancelTicket) await deleteVehicleLog(vehicle_log_id);
      } catch (e) {}
    },
    [deleteVehicleLog, showDeleteConfirm]
  );

  /** Tạo  */
  /* End form */
  const { addNotification } = useNotification();
  const currentVehicleLogCreatingPDFPassengerListing = useRef(null);
  const currentVehicleLogCreatingVehicleLogPDF = useRef(null);
  const { mutateAsync: createPassengerListing, isPending: creatingPassengerListing } = useMutateCreatePassengerListing();
  const createPassengerListingCallback = useCallback(async (trip_manifest_id) => {
    try {
      await createPassengerListing(trip_manifest_id);
      addNotification('info', 'Tạo bảng kê khách thành công! Vui lòng chờ vài giây để hệ thống tạo file và cập nhật link tải về.');
    } catch (e) {
      addNotification('error', 'Không thể tạo bảng kê khách!');
    }
  }, []);

  const { mutateAsync: createTriplog, isPending: creatingTriplog } = useMutateTripLogListing();
  const createTripLogCallback = useCallback(async (trip_manifest_id) => {
    try {
      await createTriplog(trip_manifest_id);
      addNotification('info', 'Tạo nhật trình thành công, đợi một tới hai giây để hệ thống tạo file PDF.');
    } catch (e) {
      addNotification('error', 'Không thể tạo nhật trình!');
    }
  }, []);

  /** TẢI XUỐNG */
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadVehicleLog();
  const generateDownloadLink = useCallback(
    async (vehicle_id: any) => {
      try {
        const blob = await downloadExcelThisFilter(mainQuery);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vehicle_log_${vehicle_id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {}
    },
    [mainQuery]
  );

  /** Tính tổng */
  let totalKM = 0;
  let totalIncomeFare = 0;
  let totalRecord = 0;

  const renderItem = (item: TypedVehicle_log) => {
    totalKM += item.distance || 0;
    totalRecord += 1;
    let currentIncomeFare = item?.trip_manifest?.total_fare ?? 0;
    totalIncomeFare += helpers.parseNumeric(currentIncomeFare);
    return [
      item.id,
      <div style={{ minWidth: '150px' }}>
        <Link
          onClick={() => {
            history(`/vehicle/${item.vehicle_id}/detail/${item.id}`);
          }}
        >
          {item.title || '-'}
        </Link>
      </div>,
      item.driver_data?.display_name || '-',
      item.start_address || '-',
      item.end_address || '-',
      // item.odo_start ?? '-',
      // item.odo_end ?? '-',
      item.distance ?? '-',
      helpers.formatNumberWithCommas(currentIncomeFare),
      item.start_time ?? '-',
      item.end_time ?? '-',
      item.note || '-',
      item.trip_type,

      item.createdAt ? dateandtime.format(new Date(Number(item.createdAt)), 'DD-MM-YYYY') : '-',
      <InlineStack gap="200" wrap={false}>
        {item.file_passenger_listing ? (
          <Button
            variant="plain"
            icon={PageDownIcon}
            tone="success"
            url={`${REACT_APP_AJAX_UPLOAD_PERMALINK}/${item?.file_passenger_listing}`}
            target="_blank"
          >
            Tải xuống bảng kê
          </Button>
        ) : (
          <Button
            variant="plain"
            icon={PageDownIcon}
            loading={creatingPassengerListing && currentVehicleLogCreatingPDFPassengerListing.current === item.id}
            onClick={() => {
              currentVehicleLogCreatingPDFPassengerListing.current = item.id;
              createPassengerListingCallback(item?.id);
            }}
          >
            Tạo bảng kê khách
          </Button>
        )}
        {item.file_trip_log ? (
          <Button
            variant="plain"
            icon={PageDownIcon}
            size="medium"
            tone="success"
            url={`${REACT_APP_AJAX_UPLOAD_PERMALINK}/${item?.file_trip_log}`}
            target="_blank"
          >
            Tải nhật trình
          </Button>
        ) : (
          <Button
            variant="plain"
            icon={PageDownIcon}
            loading={creatingTriplog && currentVehicleLogCreatingVehicleLogPDF.current === item.id}
            onClick={() => {
              currentVehicleLogCreatingVehicleLogPDF.current = item.id;
              createTripLogCallback(item?.id);
            }}
          >
            Tạo nhật trình
          </Button>
        )}

        <Button
          variant="plain"
          icon={DeleteIcon}
          onClick={() => {
            deleteVehicleLogCallback(item.id);
          }}
        >
          Xóa
        </Button>
        <Button
          variant="plain"
          icon={EditIcon}
          onClick={() => {
            setCurrentLogId(item.id);
            setShowModal(true);
          }}
        >
          Chỉnh
        </Button>
      </InlineStack>,
    ];
  };

  const emptyData = (
    <EmptyState heading="Không có nhật trình!" image={emptyIMG}>
      <p>Không có dữ liệu phù hợp. Hãy thử bỏ bộ lọc hoặc tìm kiếm từ khóa khác.</p>
    </EmptyState>
  );

  return (
    <BlockStack gap="400">
      <Card padding="0">
        <SimpleFilter
          loading={loadingVehicleLog}
          onCallback={filterCallback}
          options={[
            {
              label: 'PASSENGER_SERVICE',
              field: 'trip_type',
              value: 'PASSENGER_SERVICE',
            },
            {
              label: 'MAINTENANCE',
              field: 'trip_type',
              value: 'MAINTENANCE',
            },
            {
              label: 'INTERNAL_MOVE',
              field: 'trip_type',
              value: 'INTERNAL_MOVE',
            },
            {
              label: 'PRIVATE_TRIP',
              field: 'trip_type',
              value: 'PRIVATE_TRIP',
            },
            {
              label: 'INSPECTION',
              field: 'trip_type',
              value: 'INSPECTION',
            },
            {
              label: 'DELIVERY',
              field: 'trip_type',
              value: 'DELIVERY',
            },
          ]}
          sortField={[]}
          defaultSort="createdAt:desc"
        />

        {entities?.length > 0 ? (
          <DataTable
            hoverable
            stickyHeader={false}
            fixedFirstColumns={1}
            columnContentTypes={[
              'text',
              'text',
              'text',
              'text',
              'text',
              'text',
              // 'numeric',
              // 'numeric',
              'numeric',
              'numeric',
              'text',
              'text',
              'text',
              'text',
              'text',
            ]}
            headings={[
              'ID',
              'Tiêu đề',
              'Tài xế',
              'Điểm đi',
              'Điểm đến',
              // 'Km đầu',
              // 'Km cuối',
              'Khoảng cách (km)',
              'Doanh thu',
              'Bắt đầu',
              'Kết thúc',
              'Ghi chú',
              'Loại di chuyển',

              'Khởi hành',
              'Công cụ',
            ]}
            rows={entities.map(renderItem)}
            hideScrollIndicator
            // footerContent={___('Show page {display_page_number} of {total_record_number} results', {
            //   display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
            //   total_record_number: <Text as="strong">{totalItems}</Text>,
            // })}
            totals={[
              '',
              helpers.formatNumberWithCommas(totalRecord) + ' chuyến',
              '',
              '',
              '',
              helpers.formatNumberWithCommas(totalKM) + ' km',
              helpers.formatNumberWithCommas(totalIncomeFare) + ' VND',
              '',
              '',
              '',
              '',
              '',
              '',
            ]}
            totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
            showTotalsInFooter
          />
        ) : (
          emptyData
        )}
      </Card>
      <ListLogEditModal
        show={showModal}
        vehicle_id={vehicle_id}
        currentLogId={currentLogId}
        onClose={() => {
          setShowModal(false);
          setCurrentLogId(null);
        }}
      />
      <br />
      <InlineStack align="start" blockAlign="center" gap="400">
        <Button
          icon={PlusIcon}
          variant="monochromePlain"
          onClick={() => {
            setCurrentLogId(null);
            setShowModal(true);
          }}
        >
          Thêm nhật trình
        </Button>
        <Button icon={FolderDownIcon} variant="monochromePlain" onClick={() => generateDownloadLink(vehicle_id)}>
          Tải xuống XLSX bản này
        </Button>
      </InlineStack>
      <br />
      {totalItems > mainQuery.limit ? (
        <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
      ) : null}
    </BlockStack>
  );
}

export default memo(ListLog);

